import type { Flight, FlightLeg } from '../types';

export function formatMoney(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Flight fare total (per-person quote × travelers). Matches server `checkoutAmount.flightTotal`. */
export function quoteFlightTotal(pricePerPerson: number, passengerCount: number): number {
  const n = Math.max(1, Math.min(9, Math.round(passengerCount)));
  return pricePerPerson * n;
}

/** Fri, Jun 5 */
export function formatLegDateLong(travelDate?: string): string {
  if (!travelDate?.trim()) return '—';
  try {
    const d = new Date(`${travelDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return travelDate;
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return travelDate;
  }
}

/** Jun 5 */
export function formatLegDateShort(travelDate?: string): string {
  if (!travelDate?.trim()) return '—';
  try {
    const d = new Date(`${travelDate}T12:00:00Z`);
    if (Number.isNaN(d.getTime())) return travelDate;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch {
    return travelDate;
  }
}

export function formatTime12h(time?: string): string {
  const t = time?.trim();
  if (!t) return '—';
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return t;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${min} ${ampm}`;
}

/** Segment mid-line: 2:46 */
export function formatDurationColon(mins?: number | null): string {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** 1d 9h 50m */
export function formatDurationLong(mins?: number): string {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return '—';
  const d = Math.floor(mins / (24 * 60));
  const h = Math.floor((mins % (24 * 60)) / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  return parts.length ? parts.join(' ') : '—';
}

function addMinutesToIsoDate(travelDate: string, minutes: number): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(travelDate.trim());
  if (!m) return undefined;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return d.toISOString().slice(0, 10);
}

/** Arrival calendar day for the leg (departure date + block time). */
export function legArrivalDateShort(leg: FlightLeg): string {
  if (leg.travelDate && leg.totalDuration != null && leg.totalDuration > 0) {
    const iso = addMinutesToIsoDate(leg.travelDate, leg.totalDuration);
    if (iso) return formatLegDateShort(iso);
  }
  return formatLegDateShort(leg.travelDate);
}

export function summaryRouteLine(departure: string, arrival: string): string {
  return `${departure} to ${arrival}`;
}

export function purchaseSummaryRoutes(f: Flight): { route: string; date: string }[] {
  const lines: { route: string; date: string }[] = [
    { route: summaryRouteLine(f.departure, f.arrival), date: formatLegDateLong(f.travelDate) },
  ];
  if (f.returnLeg) {
    lines.push({
      route: summaryRouteLine(f.returnLeg.departure, f.returnLeg.arrival),
      date: formatLegDateLong(f.returnLeg.travelDate ?? f.returnDate),
    });
  }
  return lines;
}

/** Upsell totals from SLT OAF reference (1 passenger, selected tiers). */
export const CHECKOUT_TCS_BASIC = 192.25;
export const CHECKOUT_TCS_PREMIUM = 224.29;
export const CHECKOUT_TCS_ALL_INCLUDED = 288.37;
export const CHECKOUT_DREAMPASS_SILVER = 199.97;
export const CHECKOUT_DREAMPASS_GOLD = 299.97;
export const CHECKOUT_DREAMPASS_PLATINUM = 499.97;
export const CHECKOUT_LBP_PER_PERSON = 39.99;
/** @deprecated Legacy React payment checkout; static OAF no longer uses PDM. */
export const CHECKOUT_PDM_PER_PERSON = 70;

export function checkoutDisplayTotal(flightTotal: number): number {
  return flightTotal;
}
